import React from 'react';
import { Search, Filter } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import type { ArchivedCard, ArchivedList } from 'types/kanban';
import styles from './ArchivePanel.module.scss';

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

export interface ArchivePanelProps {
  show: boolean;
  onClose: () => void;
  archiveSearch: string;
  onArchiveSearchChange: (value: string) => void;
  archiveFilterType: 'all' | 'cards' | 'lists';
  onArchiveFilterTypeChange: (value: 'all' | 'cards' | 'lists') => void;
  archiveSort: 'date' | 'name' | 'type';
  onArchiveSortChange: (value: 'date' | 'name' | 'type') => void;
  archiveItemsGroupedByPeriod: {
    cardsInPeriod: ArchivedCard[];
    listsInPeriod: ArchivedList[];
  };
  filteredArchivedCards: ArchivedCard[];
  filteredArchivedLists: ArchivedList[];
  onRestoreCard: (a: ArchivedCard) => void;
  onRestoreList: (a: ArchivedList) => void;
  onRequestDeleteCard: (a: ArchivedCard) => void;
  onRequestDeleteList: (a: ArchivedList) => void;
}

export default function ArchivePanel({
  show,
  onClose,
  archiveSearch,
  onArchiveSearchChange,
  archiveFilterType,
  onArchiveFilterTypeChange,
  archiveSort,
  onArchiveSortChange,
  archiveItemsGroupedByPeriod,
  filteredArchivedCards,
  filteredArchivedLists,
  onRestoreCard,
  onRestoreList,
  onRequestDeleteCard,
  onRequestDeleteList,
}: ArchivePanelProps) {
  if (!show) return null;

  const olderCards = filteredArchivedCards.filter(
    (a) => new Date(a.archivedAt).getTime() < Date.now() - SEVEN_DAYS_MS,
  );
  const olderLists = filteredArchivedLists.filter(
    (a) => new Date(a.archivedAt).getTime() < Date.now() - SEVEN_DAYS_MS,
  );

  return (
    <div
      className={styles.archivePanelBackdrop}
      onClick={onClose}
      role="presentation"
      aria-hidden
    >
      <div
        className={styles.archivePanel}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Archived items"
      >
        <div className={styles.archivePanelHeader}>
          <h2 className={styles.archivePanelTitle}>Archived items</h2>
          <button
            type="button"
            className={styles.archivePanelClose}
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className={styles.archivePanelToolbar}>
          <div className={styles.archiveSearchWrap}>
            <Search size={18} className={styles.archiveSearchIcon} />
            <input
              type="text"
              className={styles.archiveSearchInput}
              placeholder="Search archived items..."
              value={archiveSearch}
              onChange={(e) => onArchiveSearchChange(e.target.value)}
              aria-label="Search archived items"
            />
          </div>
          <div className={styles.archiveFilterWrap}>
            <Filter size={16} />
            <select
              className={styles.archiveFilterSelect}
              value={archiveFilterType}
              onChange={(e) =>
                onArchiveFilterTypeChange(
                  e.target.value as 'all' | 'cards' | 'lists',
                )
              }
              aria-label="Filter by type"
            >
              <option value="all">All</option>
              <option value="cards">Cards</option>
              <option value="lists">Lists</option>
            </select>
          </div>
          <select
            className={styles.archiveSortSelect}
            value={archiveSort}
            onChange={(e) =>
              onArchiveSortChange(e.target.value as 'date' | 'name' | 'type')
            }
            aria-label="Sort by"
          >
            <option value="date">Sort by date</option>
            <option value="name">Sort by name</option>
            <option value="type">Sort by type</option>
          </select>
        </div>
        <div className={styles.archivePanelBody}>
          <section className={styles.archiveSection}>
            <h3 className={styles.archiveSectionTitle}>Past 7 days</h3>
            {archiveFilterType !== 'lists' &&
              archiveItemsGroupedByPeriod.cardsInPeriod.map((a) => (
                <div key={`card-${a.id}`} className={styles.archiveItemCard}>
                  <div className={styles.archiveItemCardPreview}>
                    <span className={styles.archiveItemTitle}>
                      {a.card.title}
                    </span>
                    <span className={styles.archiveItemMeta}>
                      Archived{' '}
                      {formatDistanceToNow(new Date(a.archivedAt), {
                        addSuffix: true,
                      })}
                    </span>
                    <span className={styles.archiveItemFrom}>
                      From: {a.originalListTitle}
                    </span>
                  </div>
                  <div className={styles.archiveItemActions}>
                    <button
                      type="button"
                      className={styles.archiveItemBtn}
                      onClick={() => onRestoreCard(a)}
                    >
                      Restore
                    </button>
                    <button
                      type="button"
                      className={styles.archiveItemBtnDanger}
                      onClick={() => onRequestDeleteCard(a)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            {archiveFilterType !== 'cards' &&
              archiveItemsGroupedByPeriod.listsInPeriod.map((a) => (
                <div key={`list-${a.id}`} className={styles.archiveItemRow}>
                  <div className={styles.archiveItemInfo}>
                    <span className={styles.archiveItemTitle}>
                      {a.column.title}
                    </span>
                    <span className={styles.archiveItemMeta}>
                      Archived{' '}
                      {formatDistanceToNow(new Date(a.archivedAt), {
                        addSuffix: true,
                      })}
                    </span>
                  </div>
                  <div className={styles.archiveItemActions}>
                    <button
                      type="button"
                      className={styles.archiveItemBtn}
                      onClick={() => onRestoreList(a)}
                    >
                      Restore
                    </button>
                    <button
                      type="button"
                      className={styles.archiveItemBtnDanger}
                      onClick={() => onRequestDeleteList(a)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
          </section>
          <section className={styles.archiveSection}>
            <h3 className={styles.archiveSectionTitle}>Older</h3>
            {archiveFilterType !== 'lists' &&
              olderCards.map((a) => (
                <div
                  key={`card-old-${a.id}`}
                  className={styles.archiveItemCard}
                >
                  <div className={styles.archiveItemCardPreview}>
                    <span className={styles.archiveItemTitle}>
                      {a.card.title}
                    </span>
                    <span className={styles.archiveItemMeta}>
                      Archived{' '}
                      {formatDistanceToNow(new Date(a.archivedAt), {
                        addSuffix: true,
                      })}
                    </span>
                    <span className={styles.archiveItemFrom}>
                      From: {a.originalListTitle}
                    </span>
                  </div>
                  <div className={styles.archiveItemActions}>
                    <button
                      type="button"
                      className={styles.archiveItemBtn}
                      onClick={() => onRestoreCard(a)}
                    >
                      Restore
                    </button>
                    <button
                      type="button"
                      className={styles.archiveItemBtnDanger}
                      onClick={() => onRequestDeleteCard(a)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            {archiveFilterType !== 'cards' &&
              olderLists.map((a) => (
                <div key={`list-old-${a.id}`} className={styles.archiveItemRow}>
                  <div className={styles.archiveItemInfo}>
                    <span className={styles.archiveItemTitle}>
                      {a.column.title}
                    </span>
                    <span className={styles.archiveItemMeta}>
                      Archived{' '}
                      {formatDistanceToNow(new Date(a.archivedAt), {
                        addSuffix: true,
                      })}
                    </span>
                  </div>
                  <div className={styles.archiveItemActions}>
                    <button
                      type="button"
                      className={styles.archiveItemBtn}
                      onClick={() => onRestoreList(a)}
                    >
                      Restore
                    </button>
                    <button
                      type="button"
                      className={styles.archiveItemBtnDanger}
                      onClick={() => onRequestDeleteList(a)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
          </section>
          {filteredArchivedCards.length === 0 &&
            filteredArchivedLists.length === 0 && (
              <p className={styles.archiveEmpty}>No archived items.</p>
            )}
        </div>
      </div>
    </div>
  );
}
